import React, {useEffect, useState} from 'react';
import Toolbar from '@mui/material/Toolbar';
import {
  List,
  Datagrid,
  Create,
  SimpleForm,
  TextField,
  TextInput,
  DateTimeInput,
  ExportButton,
  Filter,
  AutocompleteInput,
  NumberField,
  NumberInput,
  ReferenceField,
  SelectInput,
  DateField,
  BooleanField,
  ReferenceInput,
  CreateButton,
  DeleteButton,
  FormDataConsumer,
  required,
  minLength,
  maxLength,
  useRedirect,
  useRefresh,
} from 'react-admin';
import Button from '@mui/material/Button';
import { Link } from 'react-router-dom';
import dataProvider from "../../utils/dataProvider";

const COUPON_MODE_CHOICES = [
  { id: 'discount', name: 'Хямдрал' },
  { id: 'journey', name: 'Хандалт (Бие даасан хичээл)' },
];

const CodeFilter = (props) => (
  <Filter {...props}>
    <TextInput label="Code" source="code" alwaysOn />
    <TextInput label="Шошго" source="tag" alwaysOn />
  </Filter>
);

const LanguageLevelCouponActions = props => {
  const {
    filters,
    showFilter,
    resource,
    displayedFilters,
    filterValues,
    currentSort,
    total,
    exporter,
  } = props;

  return (
    <Toolbar>
      {filters &&
        React.cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
      <ExportButton
        disabled={total === 0}
        resource={resource}
        sort={currentSort}
        filter={filterValues}
        exporter={exporter}
        maxResults={total}
      />
      <CreateButton />
      <Button component={Link} to="/bulk/languageLevelCoupons/create" size="small" color="primary">Олноор үүсгэх</Button>
    </Toolbar>
  );
};

const ListFilterInput = props => {
  const {discountPlanId} = props;

  const [discountPlan, setDiscountPlan] = useState();
  useEffect(() => {
    async function fetchData(){
      if (discountPlanId) {
        const discountItem = await dataProvider.getOne(`languageLevelDiscounts`, {
          id: discountPlanId,
        });
        setDiscountPlan(discountItem.data)
      }
    }
    fetchData();
  }, [discountPlanId])
  return (
    <React.Fragment>
      <TextInput label="Дүн" initialValue={discountPlan?.sale} value={discountPlan?.sale || 0} source="amount" disabled />
    </React.Fragment>
  );
}

export const LanguageLevelCouponList = props => (
  <List {...props} filters={<CodeFilter/>} actions={<LanguageLevelCouponActions props={props} />}>
    <Datagrid optimized>
      <TextField label="ID" source="id" />
      <TextField label="Код" source="code" />
      <TextField label="Код" source="description" />
      <NumberField label="Дүн" source="amount" />
      <ReferenceField source="userId" reference="users">
        <TextField label="Нэр" source="firstName" />
      </ReferenceField>
      <ReferenceField label="Journey" source="journeyId" reference="lingo/journeys" link={false}>
        <TextField source="title" />
      </ReferenceField>
      <TextField label="Шошго" source="tag" />
      <BooleanField label="Ашигласан эсэх" source="isUsed" />
      <DateField label="Эхлэх огноо" source="startsAt" />
      <DateField label="Дуусах огноо" source="expiresAt" />
      <DeleteButton />
    </Datagrid>
  </List>
);

const transformCoupon = (data) => {
  const { _couponMode, ...rest } = data;
  if (_couponMode === 'journey') {
    const { discountPlanId, ...journeyData } = rest;
    return { ...journeyData, amount: 100 };
  }
  const { journeyId, tag, ...discountData } = rest;
  return discountData;
};

export const LanguageLevelCouponCreate = props => {
  return (
    <Create transform={transformCoupon} {...props}>
      <SimpleForm>
        <SelectInput label="Купоны төрөл" source="_couponMode" choices={COUPON_MODE_CHOICES} defaultValue="discount" />
        <TextInput label="Код" source="code" validate={[required(), minLength(6), maxLength(8)]} helperText="6 - 8 урттай үсэг тоо холилдсон том үсгээр" />
        <TextInput label="Зорилго" source="description" />
        <FormDataConsumer>
          {({ formData, ...rest }) => {
            if (formData._couponMode === 'journey') {
              return (
                <React.Fragment>
                  <ReferenceInput label="Journey (толгой багц)" source="journeyId" reference="lingo/journeys" filter={{ isParent: true, type: 'standalone' }}>
                    <SelectInput optionText="title" validate={[required()]} />
                  </ReferenceInput>
                  <TextInput label="Шошго" source="tag" />
                  <NumberInput label="Дүн (автомат 100)" source="amount" defaultValue={100} InputProps={{ readOnly: true }} />
                </React.Fragment>
              );
            }
            return (
              <React.Fragment>
                <ReferenceInput label="Хямдралын төрөл" source="discountPlanId" reference="languageLevelDiscounts">
                  <SelectInput source="discountPlanId" optionText="title" validate={[required()]} />
                </ReferenceInput>
                <ListFilterInput discountPlanId={formData && formData?.discountPlanId} {...rest} />
              </React.Fragment>
            );
          }}
        </FormDataConsumer>
        <ReferenceInput source="userId" reference="users">
          <AutocompleteInput
            source="userId"
            optionText={item => (item.email ? item.email : '')}
            shouldRenderSuggestions={(val) => { return val.trim().length > 1 }}
            options={{ suggestionsContainerProps: { disablePortal: true } }}
          />
        </ReferenceInput>
        <DateTimeInput label="Эхлэх огноо" source="startsAt" validate={required()} />
        <DateTimeInput label="Дуусах огноо" source="expiresAt" validate={required()} />
      </SimpleForm>
    </Create>
  )
};

const transformBulkCoupon = (data) => {
  const { couponCount, ...rest } = data;
  return { ...rest, number: couponCount };
};

export const BulkLanguageLevelCouponCreate = props => {
  const redirect = useRedirect();
  const refresh = useRefresh();
  const onSuccess = () => {
    redirect('/languageLevelCoupons');
    refresh();
  };

  return (
    <Create onSuccess={onSuccess} transform={transformBulkCoupon} {...props} title="Өгөгдсөн тоо ширхэгээр бие даасан хичээлийн купонуудыг үүсгэнэ">
      <SimpleForm>
        <NumberInput label="Тоо ширхэг" source="couponCount" validate={required()} helperText="Өгөгдсөн тоо ширхэгээр ижил купонуудыг үүсгэнэ" />
        <NumberInput label="Купоны кодын урт" source="codeLength" validate={required()} defaultValue={8} />
        <ReferenceInput label="Journey (толгой багц)" source="journeyId" reference="lingo/journeys" filter={{ isParent: true, type: 'standalone' }}>
          <SelectInput optionText="title" validate={[required()]} />
        </ReferenceInput>
        <TextInput label="Шошго" source="tag" />
        <DateTimeInput label="Эхлэх огноо" source="startsAt" validate={required()} />
        <DateTimeInput label="Дуусах огноо" source="expiresAt" validate={required()} />
      </SimpleForm>
    </Create>
  );
};
